// Single source of truth for the live <video>'s object-fit: cover transform.
//
// The capture screen draws one <video> full-bleed behind a 9×16 grid. The video
// is `object-fit: cover`, so the source frame is scaled up (shortest edge matches
// the box) and the overflowing edge is clipped equally on both sides. Three
// consumers need to reason about that transform:
//   - capture-image.ts  — map a display-space crop rect back to source pixels.
//   - framing.ts         — map MediaPipe landmarks into area-2-relative fractions.
//   - the guide overlay  — (indirectly) size the alignment oval to those fractions.
//
// Coordinate-space rules (read before touching landmark math):
//   - MediaPipe landmarks are normalized (0..1) over the FULL intrinsic source
//     frame (video.videoWidth × videoHeight). CSS `scaleX(-1)` and `object-fit`
//     do NOT affect them — they describe the decoded buffer, not the painted box.
//   - The cover scale is ISOTROPIC (same `scale` on x and y). Angles between
//     landmarks (e.g. the eye-line tilt) are therefore preserved through this
//     map — tilt can be measured on raw landmarks without projecting them. This
//     holds ONLY for `object-fit: cover`; switching to `fill` would skew angles.
//   - The displayed video is mirrored (CSS scaleX(-1)) but this map is NOT. It
//     produces UN-mirrored element-box coordinates. Y is never mirrored. For an
//     x-symmetric shape (oval centered at 0.5) or the gate's |fracX-0.5| test the
//     mirror is moot. Only x-ASYMMETRIC drawing tied to a landmark (a live bbox,
//     a directional arrow) needs a display flip: `displayFracX = 1 - contentFracX`.

import type { DisplayRect } from "./capture-image";

export type CoverMap = {
  scale: number;
  offsetX: number; // top-left of source content in element-box coords
  offsetY: number;
  dw: number; // video element box (CSS px)
  dh: number;
  vw: number; // intrinsic source resolution
  vh: number;
};

// Returns null when the camera or layout is not ready (any dimension is 0).
// Callers treat null as "searching" — no face / no gate decision yet.
export function computeCoverMap(video: HTMLVideoElement): CoverMap | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  // Use the on-screen (post-transform) box, not clientWidth/Height: `.app-frame`
  // is CSS-transform-scaled (see ui/overlay.ts mountFrame), so layout px and
  // visual px differ. Every other consumer (a2RectRel, crop rect) measures via
  // getBoundingClientRect, so unifying here keeps the whole map in one space.
  // The cover scale is a ratio, so the result is scale-invariant either way.
  const rect = video.getBoundingClientRect();
  const dw = rect.width;
  const dh = rect.height;
  if (!vw || !vh || !dw || !dh) return null;

  const scale = Math.max(dw / vw, dh / vh);
  const offsetX = -(vw * scale - dw) / 2;
  const offsetY = -(vh * scale - dh) / 2;
  return { scale, offsetX, offsetY, dw, dh, vw, vh };
}

// Map a display-space rect (relative to the video element box) back to source
// pixels. Used by capture-image.ts for the canvas drawImage crop.
export function displayRectToSource(map: CoverMap, rect: DisplayRect): DisplayRect {
  return {
    x: (rect.x - map.offsetX) / map.scale,
    y: (rect.y - map.offsetY) / map.scale,
    w: rect.w / map.scale,
    h: rect.h / map.scale,
  };
}

// Map a source-normalized point (0..1) to element-box coordinates (UN-mirrored).
export function sourceNormToContent(
  map: CoverMap,
  nx: number,
  ny: number,
): { x: number; y: number } {
  return {
    x: map.offsetX + nx * map.vw * map.scale,
    y: map.offsetY + ny * map.vh * map.scale,
  };
}

// Map a source-normalized point to a fraction within area-2. `a2RectRel` is the
// area-2 rect expressed relative to the video element box (i.e. area2.left -
// videoRect.left, etc.). Fractions outside [0,1] mean the point lies outside the
// viewfinder, which is exactly what the framing gate wants to detect.
export function sourceNormToArea2Frac(
  map: CoverMap,
  a2RectRel: DisplayRect,
  nx: number,
  ny: number,
): { x: number; y: number } {
  const c = sourceNormToContent(map, nx, ny);
  return {
    x: (c.x - a2RectRel.x) / a2RectRel.w,
    y: (c.y - a2RectRel.y) / a2RectRel.h,
  };
}
